/**
 * O que é um código lido? A app decide, não o operador (Fase 2c, L1). Puro —
 * corre no telemóvel antes de qualquer pedido (e com `pnpm test`).
 *
 * Ordem:
 *   1. **série conhecida** — já está nesta contagem / neste documento;
 *   2. **EAN de artigo** — é o `barcode` ou a referência interna
 *      (`default_code`, as etiquetas impressas pela maisenergia para os artigos
 *      sem EAN) de um artigo que o ecrã conhece (com ou sem dígito de controlo
 *      GS1: há códigos internos) → «leia a etiqueta com SN». Nunca série;
 *   3. **parece EAN** — dígito de controlo válido mas sem artigo conhecido: o
 *      servidor confirma (pode ser o de outro artigo);
 *   4. **não é série** — um link (o QR do fabricante) ou texto comprido;
 *   5. **nova** — série candidata.
 *
 * O servidor volta a verificar (Odoo e tabela de séries); isto só evita um
 * pedido e dá a resposta na hora.
 */

/** Como o servidor (`cleanSerial`): sem espaços, maiúsculas. */
export function normalizeCode(value: string): string {
  return String(value ?? "").replace(/\s+/g, "").toUpperCase()
}

/**
 * EAN-8, UPC-A (12), EAN-13 ou GTIN-14 (o ITF-14 das caixas) com dígito de
 * controlo GS1 válido (espelha o servidor, `isValidGtin` em `odoo/counts`).
 */
export function isValidGtin(code: string): boolean {
  if (!/^\d{8}$|^\d{12,14}$/.test(code)) return false
  const digits = code.split("").map(Number)
  const check = digits.pop()!
  const sum = digits.reverse().reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0)
  return (10 - (sum % 10)) % 10 === check
}

export type CodeKind = "serie_conhecida" | "ean_artigo" | "parece_ean" | "nao_serie" | "nova"

/** Como o servidor (`notASerial`): um link (`http://…`, `www.…`) ou texto com mais de 40 carateres. */
export function notASerial(value: string): boolean {
  const raw = String(value ?? "").trim()
  if (/^(https?:\/\/|www\.)/i.test(raw) || raw.includes("://")) return true
  return normalizeCode(raw).length > 40
}

export interface ClassifyContext {
  /** Séries já lidas nesta contagem, ou as do documento em curso. */
  knownSerials: Iterable<string>
  /** Códigos de artigos conhecidos do ecrã (EAN ou referência interna) → nome do artigo. */
  productBarcodes?: ReadonlyMap<string, string>
}

export interface Classified {
  code: string
  kind: CodeKind
  /** Nome do artigo, quando é o EAN de um artigo conhecido. */
  product?: string
}

export function classifyCode(raw: string, ctx: ClassifyContext): Classified {
  const code = normalizeCode(raw)
  const known = new Set([...ctx.knownSerials].map(normalizeCode))
  if (known.has(code)) return { code, kind: "serie_conhecida" }
  // O código de barras ou a referência de um artigo conhecido, mesmo que não
  // seja um EAN GS1 (há artigos com códigos internos no Odoo). Sem distinguir
  // maiúsculas (`normalizeCode` dos dois lados).
  for (const [barcode, product] of ctx.productBarcodes ?? []) {
    if (normalizeCode(barcode) === code) return { code, kind: "ean_artigo", product }
  }
  if (isValidGtin(code)) return { code, kind: "parece_ean" }
  if (notASerial(raw)) return { code, kind: "nao_serie" }
  return { code, kind: "nova" }
}

// ─── Série quase igual (L3; correção 11) ────────────────────────────────────

/** Pares que as etiquetas e a leitura da fatura confundem. Só estes. */
const CONFUSABLE: Record<string, string> = { O: "0", I: "1", S: "5", B: "8", Z: "2" }

function confusableForm(code: string): string {
  return code.replace(/[OISBZ]/g, (c) => CONFUSABLE[c] ?? c)
}

/**
 * Uma série do documento **quase igual** à lida (a caixa diz `AAC0Z6…`, a
 * fatura leu `AACOZ6…`): o mesmo comprimento e **todas** as diferenças entre
 * carateres confundíveis (O/0, I/1, S/5, B/8, Z/2). Uma diferença noutro
 * carater (…123 contra …124, séries seguidas) não é engano de leitura: não se
 * propõe. As séries já conferidas nas caixas não são candidatas. Só devolve
 * quando há **uma** candidata — com duas não se adivinha.
 */
export function nearMatch(raw: string, candidates: Iterable<string>, alreadyChecked: Iterable<string> = []): string | null {
  const code = normalizeCode(raw)
  const checked = new Set([...alreadyChecked].map(normalizeCode))
  const list = [...new Set([...candidates].map(normalizeCode))].filter((c) => c !== code && !checked.has(c))
  const confusable = list.filter((c) => c.length === code.length && confusableForm(c) === confusableForm(code))
  return confusable.length === 1 ? confusable[0]! : null
}
